import { lazy, Suspense } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./hooks/useAuth.js";
import { Layout } from "./components/Layout.js";
import { LoginPage } from "./pages/Login.js";

// Lazy-load heavier pages for code splitting
const DashboardPage = lazy(() =>
  import("./pages/Dashboard.js").then((m) => ({ default: m.DashboardPage }))
);
const CreateLinkPage = lazy(() =>
  import("./pages/CreateLink.js").then((m) => ({ default: m.CreateLinkPage }))
);
const LinkDetailPage = lazy(() =>
  import("./pages/LinkDetail.js").then((m) => ({ default: m.LinkDetailPage }))
);
const AnalyticsPage = lazy(() =>
  import("./pages/Analytics.js").then((m) => ({ default: m.AnalyticsPage }))
);
const SettingsPage = lazy(() =>
  import("./pages/Settings.js").then((m) => ({ default: m.SettingsPage }))
);
const InvitePage = lazy(() =>
  import("./pages/Invite.js").then((m) => ({ default: m.InvitePage }))
);
const InvitationsPage = lazy(() =>
  import("./pages/Invitations.js").then((m) => ({ default: m.InvitationsPage }))
);
const AdminDashboard = lazy(() =>
  import("./pages/admin/AdminDashboard.js").then((m) => ({ default: m.AdminDashboard }))
);
const AdminUsers = lazy(() =>
  import("./pages/admin/AdminUsers.js").then((m) => ({ default: m.AdminUsers }))
);
const AdminUserDetail = lazy(() =>
  import("./pages/admin/AdminUserDetail.js").then((m) => ({ default: m.AdminUserDetail }))
);

function PageLoader() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin h-8 w-8 border-4 border-brand-500 border-t-transparent rounded-full" />
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin h-8 w-8 border-4 border-brand-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  if (!user) {
    const returnTo = location.pathname !== "/" ? `?returnTo=${encodeURIComponent(location.pathname)}` : "";
    return <Navigate to={`/login${returnTo}`} replace />;
  }
  return <>{children}</>;
}

function OwnerRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (user?.role !== "owner") return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

const hostname = window.location.hostname;
export const isConsoleDomain = hostname === "console.ooo.codes" || hostname === "localhost";

function ConsoleRoute({ children }: { children: React.ReactNode }) {
  if (!isConsoleDomain) return <Navigate to="/dashboard" replace />;
  return <OwnerRoute>{children}</OwnerRoute>;
}

export function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/invite/:token"
          element={<InvitePage />}
        />
        <Route
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Navigate to={isConsoleDomain && hostname !== "localhost" ? "/console" : "/dashboard"} replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/links/new" element={<CreateLinkPage />} />
          <Route path="/links/:id" element={<LinkDetailPage />} />
          <Route path="/analytics/:id" element={<AnalyticsPage />} />
          <Route path="/invitations" element={<InvitationsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/console" element={<ConsoleRoute><AdminDashboard /></ConsoleRoute>} />
          <Route path="/console/users" element={<ConsoleRoute><AdminUsers /></ConsoleRoute>} />
          <Route path="/console/users/:id" element={<ConsoleRoute><AdminUserDetail /></ConsoleRoute>} />
        </Route>
      </Routes>
    </Suspense>
  );
}
